import { useTranslations } from "@jugl-web/utils";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  COMMAND_PRIORITY_LOW,
  PASTE_COMMAND,
} from "lexical";
import { FC, ForwardedRef, useEffect, useImperativeHandle } from "react";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from "@lexical/markdown";
import { $generateHtmlFromNodes } from "@lexical/html";
import { MentionTextareaHandle } from "../types";
import {
  parseTextWithMentions,
  toTextWithMentions,
  getChatMarkdownTransformers,
} from "../utils";

export interface MentionTextareaControllerPluginProps {
  handle: ForwardedRef<MentionTextareaHandle>;
  onRawTextChange?: (rawText: string) => void;
}

export const MentionTextareaControllerPlugin: FC<
  MentionTextareaControllerPluginProps
> = ({ handle, onRawTextChange }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslations();

  useImperativeHandle(handle, () => ({
    saveToJSON: () => editor.getEditorState().toJSON(),
    readFromJSON: (serializedState) =>
      editor.setEditorState(editor.parseEditorState(serializedState)),
    getRichText: () => toTextWithMentions(editor.getEditorState().toJSON()),
    setRichText: (text) =>
      editor.setEditorState(
        editor.parseEditorState(parseTextWithMentions(text, t))
      ),
    saveToMarkdown: () =>
      editor
        .getEditorState()
        .read(() =>
          $convertToMarkdownString(
            getChatMarkdownTransformers(),
            undefined,
            true
          )
        ),
    saveToHtml: () =>
      editor.getEditorState().read(() => $generateHtmlFromNodes(editor)),
    readFromMarkdown: (markdown) => {
      editor.update(() => {
        $convertFromMarkdownString(
          markdown,
          getChatMarkdownTransformers(),
          undefined,
          true
        );

        $getRoot().selectEnd();
      });
    },
    focus: () => {
      editor.focus();
    },
    insertTextAtSelection: (text: string) => {
      editor.update(() => {
        const selection = $getSelection();
        selection?.insertText(text);
      });
    },
  }));

  useEffect(() => {
    if (!onRawTextChange) {
      return undefined;
    }

    const unsubscribe = editor.registerTextContentListener(onRawTextChange);

    return unsubscribe;
  }, [editor, onRawTextChange]);

  useEffect(() => {
    const unregister = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        event.preventDefault();

        const { clipboardData } = event as ClipboardEvent;

        if (!clipboardData) {
          // No clipboard data to handle
          return false;
        }

        if (clipboardData.files.length) {
          // Prevent paste if contain files
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return unregister;
  }, [editor]);

  return null;
};
